import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Nav } from "../components/Nav";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Loader2, Check, X, ArrowRight } from "lucide-react";

export default function SubscriptionSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", payment_status: null });
  const attempts = useRef(0);
  const { refresh } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "missing", payment_status: null });
      return;
    }
    let active = true;

    const poll = async () => {
      if (!active) return;
      if (attempts.current >= 8) {
        setState((s) => ({ ...s, status: "timeout" }));
        return;
      }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        setState({ status: data.status, payment_status: data.payment_status });
        if (data.payment_status === "paid") {
          await refresh?.();
          return;
        }
        if (data.status === "expired") return;
        setTimeout(poll, 2000);
      } catch (err) {
        if (!active) return;
        toast.error("Could not verify payment");
        setState({ status: "error", payment_status: null });
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId, refresh]);

  const paid = state.payment_status === "paid";
  const failed = ["error", "missing", "timeout"].includes(state.status) || state.status === "expired";

  return (
    <div className="min-h-screen bg-[#0F0E13] text-[#F7F5F2]">
      <Nav />
      <div className="max-w-2xl mx-auto px-6 py-32 text-center fade-up">
        {!paid && !failed && (
          <>
            <Loader2 className="w-10 h-10 text-[#E3C2B0] mx-auto mb-8 animate-spin" strokeWidth={1} />
            <h1 className="font-serif text-5xl font-light">Confirming your payment...</h1>
            <p className="mt-6 text-[#A8A6A1]">A few seconds — Razorpay is whispering back.</p>
          </>
        )}
        {paid && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#E3C2B0] mx-auto flex items-center justify-center mb-8">
              <Check className="w-7 h-7 text-[#0A0908]" strokeWidth={2} />
            </div>
            <h1 className="font-serif text-5xl font-light leading-tight">Welcome to the atelier.</h1>
            <p className="mt-6 text-[#A8A6A1] max-w-md mx-auto">
              Your subscription is active. Begin composing as many looks as your imagination allows.
            </p>
            <Link
              to="/studio"
              data-testid="success-cta"
              className="inline-flex items-center gap-3 mt-12 bg-[#E3C2B0] text-[#0A0908] px-8 py-4 rounded-sm hover:bg-[#F0D4C5] transition-colors text-xs uppercase tracking-[0.3em]"
            >
              Compose a Look
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </>
        )}
        {failed && (
          <>
            <div className="w-16 h-16 rounded-full border border-red-400/40 mx-auto flex items-center justify-center mb-8">
              <X className="w-7 h-7 text-red-300" strokeWidth={2} />
            </div>
            <h1 className="font-serif text-5xl font-light">Payment incomplete.</h1>
            <p className="mt-6 text-[#A8A6A1]">No charge was made. Please try again.</p>
            <Link
              to="/subscription"
              data-testid="success-retry"
              className="inline-flex items-center gap-3 mt-12 border border-white/15 px-8 py-4 rounded-sm hover:bg-white/5 text-xs uppercase tracking-[0.3em]"
            >
              Back to Plans
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
